import { useMemo, useCallback } from 'react';

import type { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { fg } from '@confluence/feature-gating';

import { scrollCommentsPanel } from './commentsPanelHelper.ts';
import { ViewValues, useCommentsPanelActions, useCommentsPanelState } from './useCommentsPanel.ts';
import { useIsNewDanglingCommentExperienceEnabled } from './useIsNewDanglingCommentExperienceEnabled.ts';

const commentsPanelListContainerId = 'comments-panel-list-container';
const getCommentThreadContainerId = (annotationId: string) =>
	`comment-thread-${annotationId}-container`;

/**
 * @deprecated This hook is deprecated. Use handleCommentsPanelNavigation from @confluence/comments-util instead.
 * This file is kept for backward compatibility but returns an empty object.
 * All consumers should migrate to the new unified navigation functions.
 */

export const useCommentsPanelScroll = () => {
	const { currentView } = useCommentsPanelState();
	const { setCurrentView } = useCommentsPanelActions();
	const isNewDanglingCommentExperienceEnabled = useIsNewDanglingCommentExperienceEnabled();

	const scrollCommentsPanelToThread = useCallback(
		({
			annotationId,
			isCommentThreadResolved,
			isCommentThreadDangling,
			commentType,
			scrollToEnd = false,
			hasKeyPressed = false,
			annotationClicked = false,
			scrollOnLoad = false,
			commentContainerToFocus,
		}: {
			annotationId: string;
			isCommentThreadResolved?: boolean;
			isCommentThreadDangling?: boolean;
			commentType?: CommentType;
			scrollToEnd?: boolean;
			hasKeyPressed?: boolean;
			annotationClicked?: boolean;
			scrollOnLoad?: boolean;
			commentContainerToFocus?: string;
		}) => {
			if (fg('confluence_comments_unified_navigation')) {
				return;
			}

			if (!isCommentThreadResolved) {
				if (annotationClicked) {
					// Take the user to the open view if they click on an annotation
					// and they are on resolved or unread view
					if (isCommentThreadDangling && isNewDanglingCommentExperienceEnabled) {
						setCurrentView(ViewValues.DELETED_CONTENT);
					} else {
						if (
							currentView === ViewValues.UNREAD &&
							fg('confluence_frontend_unread_comments_fixes')
						) {
							return;
						} else {
							setCurrentView(ViewValues.OPEN);
						}
					}
				}
			}

			const commentThreadContainerId = getCommentThreadContainerId(annotationId);

			const scrollToThread = () => {
				scrollCommentsPanel({
					containerId: commentsPanelListContainerId,
					commentMarkerRef: commentThreadContainerId,
					commentContainerToFocus,
					commentType,
					scrollToEnd,
					hasKeyPressed,
					annotationClicked,
					scrollOnLoad,
				});
			};

			// If comment thread container exists and view is not being updated, scroll immediately
			const container = document.getElementById(commentThreadContainerId);
			if (container && currentView !== undefined) {
				scrollToThread();
				return;
			}

			// Set up observer to watch for comment thread container creation
			const observer = new MutationObserver((_mutations, obs) => {
				// observe the comment thread container
				const containerUpdated = document.getElementById(commentThreadContainerId);
				if (containerUpdated) {
					obs.disconnect();
					scrollToThread();
				}
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['id'],
			});

			// Return cleanup function to ensure observer is disconnected
			return () => {
				observer.disconnect();
			};
		},
		[currentView, setCurrentView, isNewDanglingCommentExperienceEnabled],
	);

	return useMemo(
		() => ({
			scrollCommentsPanelToThread,
		}),
		[scrollCommentsPanelToThread],
	);
};
